import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { MuiInputText } from '../controls/MuiInputText';
import './_new-editable-cell.scss';
import { fromJS } from 'immutable';
import MaterialIcon from '../../components/MaterialIcon/MaterialIcon';
import ToolTip from '../../components/controls/ToolTip';
import sharedResources from '../../features/Shared/resources';

function NewEditableCell({
    value: initialValue,
    row,
    column
}) {
    useEffect(() => {
        setValue(initialValue);
        setOldValue(initialValue);
        setIsEditMode(false);
    }, [initialValue]);
    const [value, setValue] = useState();
    const [isEditMode, setIsEditMode] = useState(false);
    const [oldValue, setOldValue] = useState();
    const [validationError, setValidationError] = useState('');
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [touched, setTouched] = useState(false);
    const props = column.getProps(row);
    const blurTimeoutRef = useRef(null);
    const onBlur = () => {
        blurTimeoutRef.current = setTimeout(() => {
            undoChangeHandler();
        }, 200);
    };
    const onFocus = () => {
        setTouched(true);
        setShowErrorMessage(false);
    };
    const onChange = (e) => {
        setValue(e.target.value);
    };
    const undoChangeHandler = () => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current); // Cancel the blur
        }
        setValue(oldValue);
        setValidationError('');
        setShowErrorMessage(false);
        setIsEditMode(!isEditMode);
        setOldValue(oldValue);
    };
    const updateCellHandler = () => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current); // Cancel the blur
        }
        for (const validate of props.validateCellRules) {
            const errorMessage = validate(value, fromJS(row.original));
            if (errorMessage) {
                setValidationError(errorMessage);
                setShowErrorMessage(true);
                return;
            }
        }
        props.updateCellHandler(value, oldValue, row.original);
        setIsEditMode(!isEditMode);
    };

    return <div className="row new-editable-cell" style={{ maxWidth: props.isEditButtonEnabled ? props.maxWidth : 'none' }}>
        {!isEditMode
            ?
            <>
                <div className="col-md-11 new-editable-cell__plain-value ellipsis">{value}</div>
                {props.isEditButtonEnabled &&
                    <div className="col-md-1">
                        <ToolTip title={props.editTooltip} arrow
                            placement="top"
                        >
                            <div>
                                <button className="new-editable-cell__button new-editable-cell__button--blue" onClick={() => setIsEditMode(!isEditMode)}
                                    disabled={props.disabled}
                                >
                                    <MaterialIcon className="new-editable-cell__button-icon" icon="edit"
                                        variant={'filled'}
                                    />
                                </button>
                            </div>
                        </ToolTip>
                    </div>}
            </>
            :
            <>
                <div className="col-md-11">
                    <ToolTip title={validationError} arrow
                        placement="top"
                        isOpen={showErrorMessage}
                        id="editableCellError"
                    >
                        <div>
                            <MuiInputText
                                autoFocus
                                input={{ value: value || '', onBlur, onChange, onFocus }}
                                meta={{ error: validationError, touched }}
                                showErrorMessage={false}
                            />
                        </div>
                    </ToolTip>
                </div>
                <div className="col-md-1" style={{ display: 'flex' }}>
                    <button className="new-editable-cell__button new-editable-cell__button--green" onClick={updateCellHandler}>
                        <MaterialIcon className="new-editable-cell__button-icon" icon="check_circle"
                            variant={'filled'}
                        />
                    </button>
                    <button className="new-editable-cell__button new-editable-cell__button--red" onClick={undoChangeHandler}>
                        <MaterialIcon className="new-editable-cell__button-icon" icon="cancel"
                            variant={'filled'}
                        />
                    </button>
                </div>
            </>}
    </div>;
};

NewEditableCell.defaultProps = {
    updateCellHandler: () => { },
    editTooltip: sharedResources.editLabel,
    maxWidth: '135px',
};

NewEditableCell.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    row: PropTypes.object.isRequired,
    column: PropTypes.object.isRequired,
    updateCellHandler: PropTypes.func.isRequired,
    validateCellRules: PropTypes.arrayOf(PropTypes.func),
    disabled: PropTypes.bool,
    isEditButtonEnabled: PropTypes.bool,
    editTooltip: PropTypes.string,
    maxWidth: PropTypes.string.isRequired,
};

export default NewEditableCell;